// Reports the visitor's machine, display and settings as Umami event "environment", once per browser tab
// session: what Umami cannot read from the user agent. Waits for window load because the tracker
// script loads after this bundle; ad blockers can stop it loading at all.
// Reference: https://umami.is/docs/tracker-functions

const SENT_KEY = 'ht-environment-sent';

const matches = (query) => window.matchMedia(query).matches;

// A stubbed or blocked client hints API must cost the event no more than these properties.
const HINTS_WAIT = 1000;

// Chromium only. The user agent says x64 and Windows 10 on every Windows machine, whatever it is.
async function system() {
  try {
    if (!navigator.userAgentData) return {};
    const hints = await Promise.race([
      navigator.userAgentData.getHighEntropyValues(['architecture', 'bitness', 'platformVersion']),
      new Promise(resolve => setTimeout(resolve, HINTS_WAIT)),
    ]);
    if (!hints) return {};
    return {
      arch: hints.architecture,
      bitness: hints.bitness,
      os_version: hints.platformVersion,
      windows: hints.platform === 'Windows' ? windowsRelease(hints.platformVersion) : undefined,
    };
  } catch {
    return {};
  }
}

// Windows reports its API contract version: 13 and up is Windows 11, and 0.1, 0.2 and 0.3 are 7, 8 and 8.1.
function windowsRelease(platformVersion) {
  const [contract, legacy] = String(platformVersion ?? '').split('.').map(Number);
  if (contract >= 13) return '11';
  if (contract > 0) return '10';
  return { 1: '7', 2: '8', 3: '8.1' }[legacy];
}

function display() {
  const scale = window.devicePixelRatio;
  return {
    // Physical pixels only at 100% zoom: Chromium's ratio includes the page zoom, its screen size does not.
    resolution: `${Math.round(screen.width * scale)}x${Math.round(screen.height * scale)}`,
    scale: Math.round(scale * 100) / 100,
    viewport_width: window.innerWidth,
    multi_monitor: screen.isExtended,
    touch: navigator.maxTouchPoints > 0,
    pointer: ['fine', 'coarse', 'none'].find(kind => matches(`(pointer: ${kind})`)),
  };
}

function settings() {
  return {
    scheme: matches('(prefers-color-scheme: dark)') ? 'dark' : 'light',
    reduced_motion: matches('(prefers-reduced-motion: reduce)'),
    forced_colors: matches('(forced-colors: active)'),
  };
}

function hardware() {
  return { cores: navigator.hardwareConcurrency, memory_gb: navigator.deviceMemory };
}

function browser() {
  // Every Chromium browser carries Chrome's version number.
  const version = /(?:Firefox|Chrome)\/(\d+)/.exec(navigator.userAgent) ?? /Version\/(\d+)/.exec(navigator.userAgent);
  return {
    browser_version: version ? Number(version[1]) : undefined,
    connection: navigator.connection?.effectiveType,
    save_data: navigator.connection?.saveData,
  };
}

// A browser that withholds an answer gives undefined or an empty string.
const answered = (data) => Object.fromEntries(
  Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== ''),
);

// Without storage every page view would count again and skew the ratios.
function alreadySent() {
  try {
    return Boolean(sessionStorage.getItem(SENT_KEY));
  } catch {
    return true;
  }
}

function markSent() {
  try {
    sessionStorage.setItem(SENT_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

window.addEventListener('load', async () => {
  if (!window.umami || alreadySent()) return;
  const data = { ...await system(), ...hardware(), ...display(), ...settings(), ...browser() };
  // Marked at the send, so a page left during the wait leaves the report to the next one.
  if (markSent()) window.umami.track('environment', answered(data));
});
